import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui';
import type { RetroDraftContent } from '../../data/types';

interface RetroDraftEditorProps {
  draft: RetroDraftContent;
  onChange: (draft: RetroDraftContent) => void;
  readOnly?: boolean;
}

const inputClass =
  'w-full px-2.5 py-1.5 text-sm rounded-md border border-border-default bg-bg-primary text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-blue disabled:opacity-70';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">
      {children}
    </h3>
  );
}

/**
 * Structured editor for the retro draft: summary, discussion topics,
 * decisions, action items. Everything is analyst-editable before publish.
 */
export function RetroDraftEditor({ draft, onChange, readOnly }: RetroDraftEditorProps) {
  const update = (patch: Partial<RetroDraftContent>) => onChange({ ...draft, ...patch });

  return (
    <div className="space-y-6">
      <section>
        <SectionHeading>Summary</SectionHeading>
        <textarea
          value={draft.summary}
          onChange={(e) => update({ summary: e.target.value })}
          disabled={readOnly}
          rows={3}
          className={`${inputClass} resize-y`}
        />
      </section>

      <section>
        <SectionHeading>Discussion topics</SectionHeading>
        <div className="space-y-3">
          {draft.topics.map((topic, i) => (
            <div key={i} className="border border-border-default rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  value={topic.title}
                  onChange={(e) => {
                    const topics = [...draft.topics];
                    topics[i] = { ...topic, title: e.target.value };
                    update({ topics });
                  }}
                  disabled={readOnly}
                  placeholder="Topic title"
                  className={`${inputClass} font-medium`}
                />
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => update({ topics: draft.topics.filter((_, j) => j !== i) })}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <textarea
                value={topic.detail}
                onChange={(e) => {
                  const topics = [...draft.topics];
                  topics[i] = { ...topic, detail: e.target.value };
                  update({ topics });
                }}
                disabled={readOnly}
                rows={3}
                className={`${inputClass} resize-y`}
              />
            </div>
          ))}
          {!readOnly && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => update({ topics: [...draft.topics, { title: '', detail: '' }] })}
            >
              <Plus className="w-4 h-4 mr-1" /> Add topic
            </Button>
          )}
        </div>
      </section>

      <section>
        <SectionHeading>Decisions</SectionHeading>
        <div className="space-y-2">
          {draft.decisions.length === 0 && readOnly && (
            <p className="text-sm text-text-secondary">No decisions recorded.</p>
          )}
          {draft.decisions.map((decision, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={decision}
                onChange={(e) => {
                  const decisions = [...draft.decisions];
                  decisions[i] = e.target.value;
                  update({ decisions });
                }}
                disabled={readOnly}
                className={inputClass}
              />
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => update({ decisions: draft.decisions.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => update({ decisions: [...draft.decisions, ''] })}
            >
              <Plus className="w-4 h-4 mr-1" /> Add decision
            </Button>
          )}
        </div>
      </section>

      <section>
        <SectionHeading>Action items</SectionHeading>
        <div className="space-y-2">
          {draft.actionItems.length === 0 && (
            <p className="text-sm text-text-secondary">
              No action items from this meeting{readOnly ? '.' : ' — that’s fine, it happens.'}
            </p>
          )}
          {draft.actionItems.map((action, i) => (
            <div key={i} className="grid grid-cols-[10rem_1fr_auto] gap-2 items-center">
              <input
                value={action.owner}
                onChange={(e) => {
                  const actionItems = [...draft.actionItems];
                  actionItems[i] = { ...action, owner: e.target.value };
                  update({ actionItems });
                }}
                disabled={readOnly}
                placeholder="Owner"
                className={inputClass}
              />
              <input
                value={action.item}
                onChange={(e) => {
                  const actionItems = [...draft.actionItems];
                  actionItems[i] = { ...action, item: e.target.value };
                  update({ actionItems });
                }}
                disabled={readOnly}
                placeholder="Action"
                className={inputClass}
              />
              {!readOnly ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    update({ actionItems: draft.actionItems.filter((_, j) => j !== i) })
                  }
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              ) : (
                <span />
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                update({ actionItems: [...draft.actionItems, { owner: '', item: '' }] })
              }
            >
              <Plus className="w-4 h-4 mr-1" /> Add action item
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
